import { isEmpty } from 'lodash';
import {
  PrintChecksRequestPayload,
  PrintChecksCIBDropdownType,
  DeleteApprovalRequestPayload,
  VoidChecksRequestPayload,
  OpenGlCalendarsType,
  PrintChecksRequestInterCompanyPayload
} from 'pages/VendorsTablePage/components/PrintChecks/types';
import { apiService, asyncApiService } from '../apiService';
import { METHOD, API_ERROR_MSG, PATHS } from '../constants';
import { validateCompanyAndStore } from '../utils';
import type {
  HeaderData,
  CheckStatusResponse,
  CheckWritingResponse,
  VoidChecksResponse
} from 'adapters/types';

export const printChecks = async (
  headers: HeaderData,
  companyId: string,
  storeId: string,
  reqObj: PrintChecksRequestPayload
): Promise<CheckWritingResponse> => {
  validateCompanyAndStore(companyId, storeId);
  const response = await asyncApiService({
    url: PATHS.getApCheckPrintPath(companyId),
    method: METHOD.POST,
    headers: { ...headers, 'x-store-id': storeId },
    reqObj
  });
  if (response.ok)
    return (await response.json()) as Promise<CheckWritingResponse>;
  const errorText = await response.text();
  throw new Error(errorText);
};

export const printInterCompanyChecks = async (
  headers: HeaderData,
  companyId: string,
  storeId: string,
  reqObj: PrintChecksRequestInterCompanyPayload,
  onUpdate: (status: CheckStatusResponse) => void
): Promise<CheckWritingResponse> => {
  validateCompanyAndStore(companyId, storeId);
  const response = await asyncApiService({
    url: PATHS.getApCheckPrintPath(companyId),
    method: METHOD.POST,
    headers: { ...headers, 'x-store-id': storeId },
    reqObj,
    onUpdate
  });
  if (response.ok)
    return (await response.json()) as Promise<CheckWritingResponse>;
  const errorText = await response.text();
  throw new Error(errorText);
};

export const deleteApproval = async (
  headers: HeaderData,
  companyId: string,
  storeId: string,
  reqObj: DeleteApprovalRequestPayload
): Promise<void> => {
  validateCompanyAndStore(companyId, storeId);
  const response = await apiService({
    url: PATHS.getApCheckApprovalsPath(companyId),
    method: METHOD.DELETE,
    headers: { ...headers, 'x-store-id': storeId },
    reqObj
  });
  if (!response?.ok) throw new Error(response.statusText);
};

export const voidChecks = async (
  headers: HeaderData,
  companyId: string,
  storeId: string,
  reqObj: VoidChecksRequestPayload
): Promise<VoidChecksResponse> => {
  validateCompanyAndStore(companyId, storeId);
  const response = await asyncApiService({
    url: PATHS.getApCheckVoidPath(companyId),
    method: METHOD.POST,
    headers: { ...headers, 'x-store-id': storeId },
    reqObj
  });
  if (response.ok)
    return (await response.json()) as Promise<VoidChecksResponse>;
  throw new Error(response.statusText);
};

export const voidAndReprintChecks = async (
  headers: HeaderData,
  companyId: string,
  storeId: string,
  voidReqPayload: VoidChecksRequestPayload,
  reprintReqPayload: PrintChecksRequestPayload
): Promise<VoidChecksResponse | CheckWritingResponse> => {
  validateCompanyAndStore(companyId, storeId);
  const voidResponse = await voidChecks(
    headers,
    companyId,
    storeId,
    voidReqPayload
  );
  if (voidResponse?.every(res => isEmpty(res.error))) {
    const printResponse = await printChecks(
      headers,
      companyId,
      storeId,
      reprintReqPayload
    );
    return printResponse;
  }
  return voidResponse;
};

export const fetchPrintChecksCIBAccounts = async (
  headers: HeaderData,
  companyId: string,
  storeId: string
): Promise<PrintChecksCIBDropdownType[]> => {
  validateCompanyAndStore(companyId, storeId);
  const response = await apiService({
    url: PATHS.getCashInBankAccountsPath(companyId),
    method: METHOD.GET,
    headers: { ...headers, 'x-store-id': storeId }
  });
  if (response.ok)
    return (await response.json()) as Promise<PrintChecksCIBDropdownType[]>;
  throw new Error(response.statusText);
};

export const fetchGlCalendar = async (
  headers: HeaderData,
  storeID: string
): Promise<OpenGlCalendarsType> => {
  if (isEmpty(storeID)) {
    throw new Error(API_ERROR_MSG.MISSING_STORE_ID);
  }
  const response = await apiService({
    url: PATHS.getGlCalendar(storeID),
    method: METHOD.GET,
    headers
  });
  if (!response?.ok) throw new Error(response.statusText);
  return response.json() as Promise<OpenGlCalendarsType>;
};
